import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuLayout from "./MenuLayout";
import CryptoNetworkLogo from "./CryptoNetworkLogo";
import "../styles/CartPage.css";
import {
  getCartItems,
  deleteCartItem,
  checkDiscount,
  checkReferral,
  getCryptoNetworks,
  initiatePayment,
} from "../api/cart";
import { getWallet } from "../api/wallet";

const cryptoCoins = [
  {
    code: "usdt_trc20",
    label: "USDT (TRC20)",
    network: "TRON",
  },
  {
    code: "usdt_bep20",
    label: "USDT (BEP20)",
    network: "BNB Smart Chain",
    recommended: true,
  },
];

const CartPage = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [discountCode, setDiscountCode] = useState("");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountMessage, setDiscountMessage] = useState("");
  const [discountLoading, setDiscountLoading] = useState(false);

  const [referralCode, setReferralCode] = useState("");
  const [referralValid, setReferralValid] = useState(null);
  const [referralMessage, setReferralMessage] = useState("");
  const [referralLoading, setReferralLoading] = useState(false);

  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);

  const [gateway, setGateway] = useState("");
  const [cryptoCurrency, setCryptoCurrency] = useState(cryptoCoins[0].code);
  const [cryptoAvailability, setCryptoAvailability] = useState({});
  const [walletDeductionInput, setWalletDeductionInput] = useState("");

  const [payLoading, setPayLoading] = useState(false);
  const [payMessage, setPayMessage] = useState("");

  const subtotal = useMemo(
    () =>
      (items || []).reduce((sum, item) => {
        const p = Number(item.price || 0);
        return sum + (Number.isNaN(p) ? 0 : p);
      }, 0),
    [items]
  );

  const discountAmount = useMemo(() => (subtotal * discountPercent) / 100, [subtotal, discountPercent]);
  const total = useMemo(() => Math.max(subtotal - discountAmount, 0), [subtotal, discountAmount]);

  const walletBalance = Number(wallet?.balance || 0);
  const isWalletTopUp = useMemo(
    () => (items || []).some((item) => item.item_type === "wallet"),
    [items]
  );
  const maxWalletDeduction = useMemo(
    () => (isWalletTopUp ? 0 : Math.min(walletBalance, total)),
    [isWalletTopUp, walletBalance, total]
  );
  const walletDeduction = useMemo(() => {
    const raw = Number(walletDeductionInput || 0);
    if (Number.isNaN(raw)) return 0;
    return Math.max(0, Math.min(raw, maxWalletDeduction));
  }, [walletDeductionInput, maxWalletDeduction]);

  const payAmount = useMemo(() => Math.max(total - walletDeduction, 0), [total, walletDeduction]);

  const loadItems = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getCartItems();
      setItems(Array.isArray(res) ? res : []);
    } catch (err) {
      setError(err?.message || "خطا در دریافت سبد خرید");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadWallet = async () => {
    setWalletLoading(true);
    try {
      const data = await getWallet();
      setWallet(data);
    } catch (err) {
      setWallet(null);
    } finally {
      setWalletLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    loadWallet();
  }, []);

  useEffect(() => {
    if (gateway !== "crypto") return;
    let cancelled = false;
    getCryptoNetworks()
      .then((response) => {
        if (cancelled) return;
        const availability = {};
        (response?.networks || []).forEach((network) => {
          availability[network.code] = Number(network.available || 0);
        });
        setCryptoAvailability(availability);
      })
      .catch(() => {
        if (!cancelled) setCryptoAvailability({});
      });
    return () => {
      cancelled = true;
    };
  }, [gateway]);

  const handleRemove = async (id) => {
    try {
      await deleteCartItem(id);
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (err) {
      setError(err?.message || "حذف آیتم سبد خرید انجام نشد");
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode) return;
    setDiscountLoading(true);
    setDiscountMessage("");
    try {
      const res = await checkDiscount(discountCode);
      if (res?.valid) {
        setDiscountPercent(Number(res.discount_percentage || 0));
        setDiscountMessage(`کد تخفیف اعمال شد (${res.discount_percentage}% تخفیف)`);
      } else {
        setDiscountPercent(0);
        setDiscountMessage(res?.message || "کد تخفیف معتبر نیست");
      }
    } catch (err) {
      setDiscountPercent(0);
      setDiscountMessage(err?.message || "خطا در بررسی کد تخفیف");
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleApplyReferral = async () => {
    if (!referralCode) return;
    setReferralLoading(true);
    setReferralMessage("");
    try {
      const res = await checkReferral(referralCode);
      const valid = res?.valid ?? res === true;
      setReferralValid(valid);
      if (valid) {
        setReferralMessage("کد ریفرال تایید شد.");
      } else {
        setReferralMessage("کد ریفرال معتبر نیست.");
      }
    } catch (err) {
      setReferralValid(false);
      setReferralMessage(err?.message || "خطا در بررسی کد ریفرال");
    } finally {
      setReferralLoading(false);
    }
  };

  const handleWalletInputChange = (e) => {
    const value = e.target.value;
    if (value === "") {
      setWalletDeductionInput("");
      return;
    }
    const raw = Number(value);
    if (Number.isNaN(raw)) return;
    const clamped = Math.max(0, Math.min(raw, maxWalletDeduction));
    setWalletDeductionInput(String(clamped));
  };

  const formatMoney = (val) => {
    const num = Number(val || 0);
    if (Number.isNaN(num)) return "-";
    return `$${num.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
  };

  const handlePay = async () => {
    if (payLoading) return;
    if (!items.length) {
      setPayMessage("سبد خرید شما خالی است.");
      return;
    }
    const walletOnly = total <= 0 || (total > 0 && walletDeduction >= total);
    if (!gateway && !walletOnly) {
      setPayMessage("درگاه را انتخاب کنید.");
      return;
    }
    if (!walletOnly && gateway === "crypto" && payAmount < 10) {
      setPayMessage("حداقل مبلغ پرداخت رمز ارز ۱۰ دلار است. برای مبالغ کمتر از کیف پول یا پرداخت ریالی استفاده کنید.");
      return;
    }
    if (!walletOnly && payAmount < 1 && total > 0) {
      setPayMessage("حداقل مبلغ قابل پرداخت 1 دلار است.");
      return;
    }

    const item = items[0];
    const payment_method = walletOnly
      ? "wallet"
      : gateway === "crypto"
        ? "internal_crypto"
        : gateway === "nowpayments"
          ? "nowpayments"
          : "paystar";
    const pay_currency = gateway === "crypto" && !walletOnly ? cryptoCurrency : undefined;
    const marketTypeRaw = item.market_type ?? (item.item_type === "competition" ? "comp" : "0");
    const platformTypeRaw = item.platform_type ?? (item.item_type === "competition" ? "comp" : "0");
    const marketType = marketTypeRaw ? String(marketTypeRaw).slice(0, 10) : "0";
    const platformType = platformTypeRaw ? String(platformTypeRaw).slice(0, 10) : "0";

    const payload = {
      payment_method,
      amount: walletOnly ? 0 : payAmount || 1,
      pay_currency,
      item_type: item.item_type,
      name: item.name,
      market_type: marketType,
      investment_amount: Number(item.investment_amount ?? item.price ?? 0) || 0,
      price: total,
      platform_type: platformType,
      broker_type: item.broker_type ?? "0",
      wallet_deduction: walletOnly ? total : walletDeduction,
      direct_payment_amount: walletOnly ? 0 : payAmount,
      referral_code: referralCode || undefined,
      discount_code: discountCode || undefined,
    };

    setPayLoading(true);
    setPayMessage("");
    try {
      const res = await initiatePayment(payload);

      if (res?.payment_url) {
        window.location.href = res.payment_url;
        return;
      }

      if (res?.payment_type === "internal_crypto" && res?.payment_data) {
        navigate(`/crypto-payment/${res.payment_data.payment_id}`, {
          state: { payment: res.payment_data },
        });
        return;
      }

      if (res?.wallet_paid || res?.status === "success") {
        navigate("/orders");
        return;
      }

      setPayMessage("درخواست پرداخت ثبت شد.");
    } catch (err) {
      setPayMessage(err?.message || "خطا در ایجاد پرداخت");
    } finally {
      setPayLoading(false);
    }
  };

  return (
    <MenuLayout>
      <div className="cart-page">
        <header className="cart-header">
          <h1 className="cart-title">سبد خرید</h1>
          <p className="cart-subtitle">
            پلن انتخابی را بررسی کنید، تخفیف/ریفراِل و کسر کیف پول را اعمال کنید و پرداخت را ادامه دهید.
          </p>
        </header>

        {error && <div className="cart-error">{error}</div>}
        {payMessage && <div className="cart-hint">{payMessage}</div>}

        <div className="cart-grid">
          <div className="cart-right">
            <div className="cart-card challenge">
              <div className="cart-card-header">جزئیات سبد خرید</div>
              {loading ? (
                <div className="cart-empty">در حال دریافت...</div>
              ) : items.length === 0 ? (
                <div className="cart-empty">سبد خرید شما خالی است.</div>
              ) : (
                items.map((item) => (
                  <div className="challenge-row" key={item.id}>
                    <div className="challenge-col">
                      <span className="challenge-value success">{item.name || item.item_type}</span>
                      <span className="challenge-meta">
                        {item.item_type === "wallet" ? "شارژ کیف پول" : item.item_type || ""}
                      </span>
                    </div>
                    <div className="challenge-price">{formatMoney(item.price)}</div>
                    <button className="challenge-remove" onClick={() => handleRemove(item.id)}>
                      حذف
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="cart-left">
            <div className="cart-card thin">
              <div className="cart-card-header">فاکتور</div>
              <div className="cart-row">
                <span className="cart-row-label">تعداد آیتم:</span>
                <span className="cart-row-value link">{items.length} مورد</span>
              </div>
              <div className="cart-row">
                <span className="cart-row-label">جمع جزء:</span>
                <span className="cart-row-value price">{formatMoney(subtotal)}</span>
              </div>
              <div className="cart-row">
                <span className="cart-row-label">تخفیف:</span>
                <span className="cart-row-value price">-{formatMoney(discountAmount)}</span>
              </div>
            </div>

            <div className="cart-card thin">
              <div className="cart-card-header">کد تخفیف</div>
              <label className="cart-input-label" htmlFor="coupon">
                کد تخفیف:
              </label>
              <div className="cart-discount-input">
                <input
                  id="coupon"
                  type="text"
                  placeholder="مثال: 12cx131x"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                />
                <button className="cart-apply-btn" onClick={handleApplyDiscount} disabled={discountLoading}>
                  {discountLoading ? "در حال بررسی..." : "اعمال"}
                </button>
              </div>
              {discountMessage && <div className="cart-hint">{discountMessage}</div>}
            </div>

            <div className="cart-card thin">
              <div className="cart-card-header">کد ریفرال</div>
              <label className="cart-input-label" htmlFor="referral">
                کد ریفرال:
              </label>
              <div className="cart-discount-input">
                <input
                  id="referral"
                  type="text"
                  placeholder="مثال: d1rwz"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                />
                <button className="cart-apply-btn" onClick={handleApplyReferral} disabled={referralLoading}>
                  {referralLoading ? "در حال بررسی..." : "اعمال"}
                </button>
              </div>
              {referralMessage && (
                <div className={`cart-hint ${referralValid ? "success" : "danger"}`}>{referralMessage}</div>
              )}
            </div>

            <div className="cart-card thin">
              <div className="cart-card-header">کسر از کیف پول</div>
              <div className="cart-row">
                <span className="cart-row-label">موجودی کیف پول:</span>
                <span className="cart-row-value">{walletLoading ? "..." : formatMoney(walletBalance)}</span>
              </div>
              {isWalletTopUp ? (
                <div className="cart-hint">
                  برای شارژ کیف پول امکان پرداخت از موجودی همان کیف پول وجود ندارد.
                </div>
              ) : (
                <>
                  <label className="cart-input-label" htmlFor="wallet-deduction">
                    مبلغ کسر از کیف پول (حداکثر {formatMoney(maxWalletDeduction)}):
                  </label>
                  <div className="cart-wallet-input">
                    <input
                      id="wallet-deduction"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="مثلا 25"
                      value={walletDeductionInput}
                      onChange={handleWalletInputChange}
                    />
                    <button
                      type="button"
                      className="cart-wallet-max"
                      onClick={() => setWalletDeductionInput(String(maxWalletDeduction))}
                      disabled={maxWalletDeduction <= 0}
                    >
                      حداکثر
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="cart-card">
              <div className="cart-card-header">مبلغ قابل پرداخت</div>
              <div className="cart-total">{formatMoney(payAmount)}</div>

              <label className="cart-input-label" htmlFor="gateway">
                درگاه پرداخت:
              </label>
              <div className="cart-select">
                <select id="gateway" value={gateway} onChange={(e) => setGateway(e.target.value)}>
                  <option value="" disabled>
                    درگاه را انتخاب کنید
                  </option>
                  <option value="paystar">پی استار (تومان) </option>
                  <option value="crypto">پرداخت رمز ارزی</option>
                </select>
              </div>

              {gateway === "paystar" && (
                <div className="cart-gateway-note">
                  پیش از پرداخت ریالی حتما فیلترشکن خود را خاموش کنید و ادامه دهید.
                </div>
              )}

              {gateway === "crypto" && (
                <div className="cart-gateway-note danger" role="alert">
                  <strong>⚠ هشدار پرداخت رمزارزی</strong>
                  <span>
                    حداقل مبلغ پرداخت ۱۰ دلار است. شبکه را با دقت انتخاب کنید؛
                    ارسال به آدرس یا شبکه اشتباه ممکن است باعث از دست رفتن دائمی
                    دارایی شما شود.
                  </span>
                </div>
              )}

              {gateway === "crypto" && (
                <div className="crypto-network-grid">
                  {cryptoCoins.map((c) => (
                    <button
                      key={c.code}
                      type="button"
                      className={`crypto-network-card ${cryptoCurrency === c.code ? "active" : ""}`}
                      onClick={() => setCryptoCurrency(c.code)}
                      disabled={cryptoAvailability[c.code] === 0}
                    >
                      <CryptoNetworkLogo
                        network={c.code}
                        className="crypto-network-logo"
                      />
                      <span className="crypto-network-copy">
                        <strong>{c.label}</strong>
                        <small>{c.network}</small>
                        {c.recommended && (
                          <span className="crypto-recommended">
                            پیشنهاد ما · کارمزد کمتر
                          </span>
                        )}
                        {cryptoAvailability[c.code] === 0 && (
                          <span className="crypto-busy">در حال حاضر صف این شبکه پر است</span>
                        )}
                      </span>
                      <span className="crypto-network-check" aria-hidden="true">
                        ✓
                      </span>
                    </button>
                  ))}
                </div>
              )}

              <button className="cart-submit" onClick={handlePay} disabled={payLoading || loading}>
                {payLoading ? "در حال ایجاد پرداخت..." : "ادامه و پرداخت"}
              </button>
            </div>

          </div>
        </div>
      </div>
    </MenuLayout>
  );
};

export default CartPage;
